import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { C } from './constants';
import { BoutonRemplirMihi } from './ChiffresMihi';
import { getPeriodeInfo, DecouverteTour } from './App';
import {
  calculerEquipe, NIVEAUX, STATUTS_DIR, PV_MIN, SEUIL_DIRECTEUR,
} from './PlanMarketing';

// ─────────────────────────────────────────────────────────────────────────────
// 👥 GAINS DE L'ÉQUIPE : les personnes de l'équipe sont cherchées dans l'annuaire,
// leur chiffre remonte seul ; si une personne ne se met pas à jour, on le tape à la main.
// Le calcul suit le plan marketing (voir PlanMarketing.js).
// Données enregistrées (dans db-tirelire.equipe) :
//   { pv, nbElite, enfants, membres:[{id, uid, nom, pgvManuel, pvManuel, ligne, bonus2}] }
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// Feuille modale commune (tirelire, recrues, équipe)
//  - affichée directement dans <body> : passe au-dessus des boutons ronds flottants de l'appli
//  - hauteur calée sur l'écran visible du téléphone (dvh) : plus de bas de fenêtre caché
//  - défile toujours, et la page derrière ne bouge plus
// ─────────────────────────────────────────────────────────────────────────────
let verrousScroll = 0;
let ancienOverflow = "";
const CSS_FEUILLE = `
.tl-feuille{max-height:88vh;max-height:calc(100dvh - 48px);overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;touch-action:pan-y;}
.tl-feuille::after{content:"";display:block;height:.2rem;}
`;
const hauteurVisible = () => {
  try {
    const vv = window.visualViewport;
    const h = Math.min(window.innerHeight || 0, vv && vv.height ? vv.height : 99999);
    return h > 100 ? Math.round(h) : 0;
  } catch { return 0; }
};
export function FeuilleModale({ onClose, z = 9000, fond = "rgba(40,20,10,.62)", children, padding = "1rem 1rem 1.1rem" }) {
  // Hauteur réellement visible à l'écran (barres du navigateur déduites) : la fenêtre ne dépasse jamais l'écran
  const [hVisible, setHVisible] = useState(hauteurVisible);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const maj = () => setHVisible(hauteurVisible());
    maj();
    window.addEventListener("resize", maj);
    window.addEventListener("orientationchange", maj);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", maj);
    return () => {
      window.removeEventListener("resize", maj);
      window.removeEventListener("orientationchange", maj);
      if (vv) vv.removeEventListener("resize", maj);
    };
  }, []);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (verrousScroll === 0) { ancienOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; }
    verrousScroll += 1;
    return () => {
      verrousScroll = Math.max(0, verrousScroll - 1);
      if (verrousScroll === 0) document.body.style.overflow = ancienOverflow;
    };
  }, []);
  const noeud = (
    <div onClick={onClose} style={{ position: "fixed", top: 0, left: 0, right: 0, height: hVisible ? hVisible + "px" : "100%", background: fond, zIndex: z, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 10px calc(16px + env(safe-area-inset-bottom, 0px))", boxSizing: "border-box" }}>
      <style>{CSS_FEUILLE}</style>
      <div className="tl-feuille" onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", background: C.creme, width: "100%", maxWidth: 480, borderRadius: 22, padding, boxSizing: "border-box", boxShadow: "0 8px 30px rgba(40,20,10,.35)", ...(hVisible ? { maxHeight: (hVisible - 52) + "px" } : {}) }}>
        {children}
      </div>
    </div>
  );
  return typeof document !== "undefined" && document.body ? createPortal(noeud, document.body) : noeud;
}

const nombre = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/\s/g, "").replace(",", "."));
  return isNaN(n) || n < 0 ? 0 : n;
};
const vide = (v) => v === undefined || v === null || String(v).trim() === "";
const arrondi = (n) => Math.round(n * 100) / 100;
const fmtE = (n) => {
  const v = arrondi(n);
  return v.toLocaleString("fr-FR", Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const fmtPct = (p) => String(p).replace(".", ",") + " %";
const idUnique = () => Date.now() * 1000 + Math.floor(Math.random() * 1000);

export const EQUIPE_VIDE = { pv: "", nbElite: 0, enfants: 0, membres: [] };

// Confidentialité : chacune ne voit que SES chiffres et ceux des personnes arrivées SOUS elle (ses filleules, directes et indirectes).
// Melissa (cheffe de la lignée) voit tout le monde.
export const estMelissaUid = (u) => u === "melissa" || u === "melissa-da-silveira";

export function lignee(uid, annuaire) {
  const enfants = enfantsParMarraine(annuaire || {});
  const res = new Set();
  const pile = [...(enfants[uid] || [])];
  while (pile.length) {
    const u = pile.pop();
    if (res.has(u) || u === uid) continue;
    res.add(u);
    (enfants[u] || []).forEach((c) => pile.push(c));
  }
  return res;
}

// L'annuaire tel que la personne a le droit de le voir : elle-même + sa lignée (rien d'autre)
export function filtrerAnnuaire(annuaire, uid) {
  if (!annuaire) return annuaire;
  if (!uid) return {};
  if (estMelissaUid(uid)) return annuaire;
  const sous = lignee(uid, annuaire);
  const out = {};
  Object.entries(annuaire).forEach(([k, a]) => {
    const u = (a && a.uid) || k;
    if (u === uid || sous.has(u)) out[k] = a;
  });
  return out;
}

// Chiffres Mihi partagés : coffre « chiffres_equipe » (la règle Firestore ne laisse lire que sa propre fiche et celles de sa lignée)
async function lireChiffresEquipe(uid) {
  const res = {};
  try {
    const moi = await getDoc(doc(db, "chiffres_equipe", uid));
    if (moi.exists()) res[uid] = moi.data();
  } catch { /* pas de chiffres Mihi */ }
  try {
    const q = estMelissaUid(uid) ? collection(db, "chiffres_equipe") : query(collection(db, "chiffres_equipe"), where("ancetres", "array-contains", uid));
    const snap = await getDocs(q);
    snap.forEach((d) => { res[d.id] = d.data(); });
  } catch { /* pas de chiffres Mihi */ }
  return res;
}

export async function chargerAnnuaire(uid) {
  try {
    const snap = await getDoc(doc(db, "equipe", "annuaire"));
    const tout = snap.exists() ? (snap.data().membres || {}) : {};
    const vu = filtrerAnnuaire(tout, uid);   // sans uid : rien n'est montré
    if (!uid) return vu;
    const coffre = await lireChiffresEquipe(uid);
    const out = {};
    Object.entries(vu).forEach(([k, a]) => {
      // les anciens chiffres Mihi restés dans l'annuaire commun sont ignorés : seul le coffre fait foi
      const { caMihi, caPersoMihi, majMihi, periodeMihi, ...propre } = a || {};
      const c = coffre[(a && a.uid) || k];
      out[k] = c ? { ...propre, caMihi: c.caMihi, majMihi: c.majMihi, periodeMihi: c.periodeMihi } : propre;
    });
    return out;
  } catch {
    return null;
  }
}

const debutPeriodeMs = () => {
  try { const p = getPeriodeInfo(); return p && p.periodStart ? new Date(p.periodStart).getTime() : 0; } catch { return 0; }
};

// ── Arbre des marraines : permet de compter correctement les « filleules de mes filleules » ──
const enfantsParMarraine = (annuaire) => {
  const map = {};
  Object.entries(annuaire || {}).forEach(([k, a]) => {
    const u = (a && a.uid) || k;
    if (a && a.marraine) (map[a.marraine] = map[a.marraine] || []).push(u);
  });
  return map;
};
// Chiffres envoyés depuis Mihi par sa cheffe d'équipe (champs à part : ils n'écrasent jamais ce que la personne a tapé elle-même)
const mihiFrais = (a, debut) => !!a && Number(a.majMihi) > 0 && Number(a.majMihi) >= debut && !vide(a.caMihi);
const pvActif = (a, debut) => (a && (a.lastActive || 0) >= debut && !vide(a.caPerso) ? nombre(a.caPerso) : 0);

// Volume du groupe d'une personne = son PV + celui de toute son équipe (sauf les groupes de directrices, qui sortent du PGV)
function volumeArbre(uid, annuaire, enfants, debut, vus = new Set()) {
  if (vus.has(uid)) return { pgv: 0, nb: 0, pv: 0 };
  vus.add(uid);
  const a = annuaire[uid];
  const pv = pvActif(a, debut);
  let pgv = pv;
  let nb = 0;
  (enfants[uid] || []).forEach((c) => {
    const sous = volumeArbre(c, annuaire, enfants, debut, vus);
    const estDirectrice = sous.pgv >= SEUIL_DIRECTEUR && sous.pv >= PV_MIN;
    if (!estDirectrice) { pgv += sous.pgv; nb += sous.nb + (sous.pv > 0 ? 1 : 0); }
  });
  return { pgv: arrondi(pgv), nb, pv };
}

// Si quelqu'un de ma liste est déjà dans le groupe d'une autre personne de ma liste, son volume est déjà compté chez elle
export function ancetreDansListe(uid, annuaire, uidsChoisis) {
  if (!uid || !annuaire) return null;
  const vus = new Set([uid]);
  let cur = annuaire[uid] && annuaire[uid].marraine;
  while (cur && !vus.has(cur)) {
    vus.add(cur);
    if (uidsChoisis.has(cur)) return cur;
    cur = annuaire[cur] && annuaire[cur].marraine;
  }
  return null;
}

// Valeurs réellement utilisées pour chaque personne : saisie à la main > annuaire (si à jour cette période)
export function membresEffectifs(equipe, annuaire) {
  const debut = debutPeriodeMs();
  const membres = (equipe && equipe.membres) || [];
  const enfants = annuaire ? enfantsParMarraine(annuaire) : {};
  const uidsChoisis = new Set(membres.map((m) => m.uid).filter(Boolean));
  return membres.map((m) => {
    const a = m.uid && annuaire ? annuaire[m.uid] : null;
    const mihi = mihiFrais(a, debut);                           // chiffres Mihi de cette période : prioritaires
    const obsolete = !!a && !mihi && (a.lastActive || 0) < debut;       // pas reconnectée depuis le début de la période
    const caAnnuaire = mihi && !vide(a.caMihi) ? nombre(a.caMihi) : (a && !obsolete && !vide(a.ca) ? nombre(a.ca) : null);
    const pvAnnuaire = a && !obsolete && !vide(a.caPerso) ? nombre(a.caPerso) : null;
    // Volume recalculé à partir de toute son équipe : on garde le plus grand des deux (le vrai volume ne peut pas être plus petit)
    const arbre = a ? volumeArbre(m.uid, annuaire, enfants, debut) : { pgv: 0, nb: 0, pv: 0 };
    const pgvAuto = Math.max(caAnnuaire != null ? caAnnuaire : 0, arbre.pgv);
    const aAuto = caAnnuaire != null || arbre.pgv > 0;
    const manuelPgv = !vide(m.pgvManuel);
    const manuelPv = !vide(m.pvManuel);
    const pgv = manuelPgv ? nombre(m.pgvManuel) : (aAuto ? pgvAuto : 0);
    const pvConnu = manuelPv ? nombre(m.pvManuel) : pvAnnuaire;
    const ancetre = ancetreDansListe(m.uid, annuaire, uidsChoisis);
    const ancetreM = ancetre ? membres.find((x) => x.uid === ancetre) : null;
    return {
      ...m,
      pgv,
      pvOk: pvConnu == null ? null : pvConnu >= PV_MIN,
      auto: manuelPgv ? null : (aAuto ? pgvAuto : null),
      source: manuelPgv ? "manuel" : (!a ? "manuel-vide" : aAuto ? (obsolete ? "arbre-seul" : "auto") : obsolete ? "obsolete" : "vide"),
      maj: a ? (mihi ? a.majMihi : a.lastActive) || null : null,
      nbSous: arbre.nb,
      depuisEquipe: !manuelPgv && aAuto && arbre.pgv > (caAnnuaire != null ? caAnnuaire : 0),
      exclu: !!ancetreM,
      inclusDans: ancetreM ? ancetreM.nom : null,
    };
  });
}

// Résultat complet (utilisé par le panneau ET par la tirelire en arrière-plan)
export function resultatEquipe(equipe, annuaire, pvDefaut = 0) {
  const e = equipe || EQUIPE_VIDE;
  const pv = !vide(e.pv) ? nombre(e.pv) : nombre(pvDefaut);
  return calculerEquipe({ pv, membres: membresEffectifs(e, annuaire).filter((m) => !m.exclu), nbElite: e.nbElite, enfants: e.enfants });
}

const dateCourte = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

// Flèches pour ranger les personnes dans l'ordre voulu
export function FlechesOrdre({ onHaut, onBas, premier, dernier }) {
  const b = (actif) => ({ background: "none", border: "none", padding: "0 .15rem", lineHeight: 1, fontSize: ".72rem", cursor: actif ? "pointer" : "default", color: actif ? "#8a6d5a" : "#d9cfc8", fontFamily: "inherit" });
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      <button type="button" aria-label="Monter" disabled={premier} onClick={onHaut} style={b(!premier)}>▲</button>
      <button type="button" aria-label="Descendre" disabled={dernier} onClick={onBas} style={b(!dernier)}>▼</button>
    </div>
  );
}

// Déplace l'élément d'identifiant `id` de `delta` place(s) dans la liste
export function deplacerDansListe(liste, id, delta) {
  const i = liste.findIndex((x) => x.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= liste.length) return liste;
  const copie = liste.slice();
  const [x] = copie.splice(i, 1);
  copie.splice(j, 0, x);
  return copie;
}

const bloc = { background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 14, padding: ".9rem", marginBottom: ".7rem" };
const etiquette = { fontSize: ".62rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.rose, marginBottom: ".4rem" };
const champ = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${C.pale}`, borderRadius: 10, padding: ".55rem .7rem", fontSize: ".95rem", fontFamily: "inherit", color: C.brun, background: C.creme, outline: "none", fontWeight: 600 };
const petitBtn = (actif) => ({ padding: ".32rem .7rem", fontSize: ".68rem", fontWeight: 700, borderRadius: 20, cursor: "pointer", fontFamily: "inherit", border: `1.5px solid ${actif ? C.rose : C.pale}`, background: actif ? C.rose : C.blanc, color: actif ? "white" : C.gris });

export function EquipePanel({ uid, equipe, onChange, onGain, pvDefaut = 0, onClose }) {
  const [annuaire, setAnnuaire] = useState(undefined);       // undefined = chargement, null = indisponible
  const [recherche, setRecherche] = useState("");
  const [manuelNom, setManuelNom] = useState("");
  const [manuelPgv, setManuelPgv] = useState("");
  const [dirOuvert, setDirOuvert] = useState(false);
  const [infosOuvert, setInfosOuvert] = useState(false);
  const [tour, setTour] = useState(false);
  const e = equipe || EQUIPE_VIDE;

  useEffect(() => {
    let vivant = true;
    chargerAnnuaire(uid).then((a) => { if (vivant) setAnnuaire(a); });
    return () => { vivant = false; };
  }, [uid]);

  const eff = useMemo(() => membresEffectifs(e, annuaire || null), [e, annuaire]);
  const res = useMemo(() => resultatEquipe(e, annuaire || null, pvDefaut), [e, annuaire, pvDefaut]);

  // Prévient la tirelire du nouveau gain d'équipe
  const dernierGain = useRef(null);
  useEffect(() => {
    if (annuaire === undefined) return;
    if (dernierGain.current !== res.total) { dernierGain.current = res.total; onGain(res.total); }
  }, [res.total, annuaire, onGain]);

  const maj = (patch) => onChange({ ...e, ...patch });
  const majMembre = (id, patch) => maj({ membres: (e.membres || []).map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  const retirer = (id) => maj({ membres: (e.membres || []).filter((m) => m.id !== id) });
  const deplacer = (id, delta) => maj({ membres: deplacerDansListe(e.membres || [], id, delta) });

  const dejaLa = new Set((e.membres || []).map((m) => m.uid).filter(Boolean));
  const nomAnnuaire = (a) => `${a.prenom || ""} ${a.nom || ""}`.trim() || a.uid;
  const toutes = annuaire ? Object.entries(annuaire).map(([k, a]) => ({ ...a, uid: a.uid || k })).filter((a) => a.uid !== uid && !dejaLa.has(a.uid)) : [];
  const directes = toutes.filter((a) => a.marraine === uid);
  const q = recherche.trim().toLowerCase();
  const resultats = q.length >= 2
    ? toutes.filter((a) => nomAnnuaire(a).toLowerCase().includes(q)).sort((x, y) => (y.marraine === uid) - (x.marraine === uid)).slice(0, 8)
    : [];
  const uidsChoisis = new Set((e.membres || []).map((m) => m.uid).filter(Boolean));
  const dansGroupeDe = (a) => {
    const anc = ancetreDansListe(a.uid, annuaire, uidsChoisis);
    const m = anc ? (e.membres || []).find((x) => x.uid === anc) : null;
    return m ? m.nom : null;
  };

  const ajouterAnnuaire = (liste) => {
    if (!liste.length) return;
    maj({ membres: [...(e.membres || []), ...liste.map((a) => ({ id: idUnique(), uid: a.uid, nom: nomAnnuaire(a), pgvManuel: "", pvManuel: "", ligne: 1, bonus2: false }))] });
    setRecherche("");
  };
  const ajouterManuel = () => {
    const nom = manuelNom.trim();
    if (!nom) return;
    maj({ membres: [...(e.membres || []), { id: idUnique(), uid: null, nom, pgvManuel: manuelPgv, pvManuel: "", ligne: 1, bonus2: false }] });
    setManuelNom(""); setManuelPgv("");
  };

  const parId = new Map();
  res.lignes.forEach((l) => parId.set(l.id, { type: "groupe", ...l }));
  res.directrices.forEach((d) => parId.set(d.id, { type: "directrice", ...d, prime: res.primes.find((p) => p.id === d.id) }));

  const suivantStatut = res.prochainStatut;
  const manqueGroupes = suivantStatut ? Math.max(0, suivantStatut.groupes - res.nbGroupes1) : 0;
  const pctBarre = res.suivant ? Math.min(100, Math.round((res.pgv / res.suivant.pgv) * 100)) : 100;

  return (
    <FeuilleModale onClose={onClose} z={9100} fond="rgba(40,20,10,.7)">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".6rem" }}>
          <div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", color: C.brun }}>Mon <em style={{ color: C.rose }}>équipe</em></div>
            <div style={{ fontSize: ".62rem", color: C.gris }}>Ce que ton équipe te fait gagner (plan marketing Mihi)</div>
          </div>
          <button onClick={onClose} aria-label="Fermer" style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: "50%", width: 34, height: 34, fontSize: "1rem", color: C.gris, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: ".5rem" }}>
          <button onClick={() => setTour(true)} style={{ background: "#C49A8A", color: "white", border: "none", borderRadius: 20, padding: ".35rem 1rem", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(196,154,138,.4)" }}>🧭 Découverte</button>
        </div>
        {tour && <DecouverteTour outil="equipe" onClose={() => setTour(false)} />}

        {/* RÉSULTAT */}
        <div id="decouverte-equipe-resultat" style={{ background: `linear-gradient(135deg,${C.brun},${C.brun2 || C.brun})`, borderRadius: 16, padding: "1rem", marginBottom: ".7rem", color: "white" }}>
          <div style={{ fontSize: ".6rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.or }}>Grâce à mon équipe</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 600, lineHeight: 1.15 }}>{fmtE(res.total)}</div>
          <div style={{ fontSize: ".72rem", opacity: .85, marginBottom: ".5rem" }}>
            Niveau : <strong>{res.panier ? "Mentor d'un groupe de directrices" : res.estDirectrice ? (res.statut ? res.statut.nom : "Directrice") : res.niveau.nom}</strong> · {fmtPct(res.pct)} · mon PGV {fmtE(res.pgv)}
          </div>
          {!res.pvOk && (
            <div style={{ background: "rgba(230,57,70,.25)", border: "1px solid rgba(255,255,255,.35)", borderRadius: 10, padding: ".5rem .65rem", fontSize: ".72rem", lineHeight: 1.5, marginBottom: ".5rem" }}>
              ⚠️ Il faut au moins <strong>{PV_MIN} €</strong> de volume personnel (PV) sur la période. Sans ça, rien n'est versé et ton volume remonte à ta marraine.
            </div>
          )}
          {res.suivant && res.pvOk && (
            <>
              <div style={{ height: 7, background: "rgba(255,255,255,.2)", borderRadius: 10, overflow: "hidden", marginBottom: ".3rem" }}>
                <div style={{ height: "100%", width: Math.max(3, pctBarre) + "%", background: "linear-gradient(90deg,#F6D060,#E0A93B)", borderRadius: 10, transition: "width .6s" }} />
              </div>
              <div style={{ fontSize: ".68rem", opacity: .9 }}>
                Plus que <strong>{fmtE(res.manque)}</strong> de volume pour devenir <strong>{res.suivant.nom}</strong> ({fmtPct(res.suivant.pct)})
              </div>
            </>
          )}
          <div style={{ marginTop: ".6rem", display: "grid", gridTemplateColumns: "1fr auto", rowGap: ".15rem", fontSize: ".7rem", opacity: .92 }}>
            <span>Sur mon volume perso</span><strong>{fmtE(res.gainPV)}</strong>
            <span>Sur mon équipe (différence de %)</span><strong>{fmtE(res.gainGroupe)}</strong>
            {(res.gainPrimes > 0 || res.estDirectrice) && <><span>Primes de directrice</span><strong>{fmtE(res.gainPrimes)}</strong></>}
            {res.gainParental > 0 && <><span>Prime parentale</span><strong>{fmtE(res.gainParental)}</strong></>}
          </div>
        </div>

        {/* CHIFFRES MIHI : seulement le volume de groupe (PGV) de chaque personne. Jamais le volume perso, jamais les ventes. */}
        {(e.membres || []).length > 0 && (
          <div id="decouverte-equipe-mihi" style={{ marginBottom: ".4rem" }}>
            <BoutonRemplirMihi
              uid={uid}
              titre="Remplir le PGV de l'équipe depuis Mihi"
              aide="Reprend uniquement le volume de groupe (PGV) de chaque personne de ta liste. Tes ventes et ton volume perso ne sont jamais touchés."
              items={(e.membres || []).map((m) => ({ key: m.id, nom: m.nom, uid: m.uid || null }))}
              decrire={(mb) => `PGV ${fmtE(mb.vgp)}`}
              onApply={(liste) => {
                const parId = {};
                liste.forEach(({ key, membre }) => { parId[key] = membre; });
                maj({ membres: (e.membres || []).map((m) => (parId[m.id] ? { ...m, pgvManuel: String(Math.round(parId[m.id].vgp * 100) / 100) } : m)) });
              }}
            />
          </div>
        )}

        {/* MON VOLUME PERSO */}
        <div id="decouverte-equipe-pv" style={bloc}>
          <div style={etiquette}>🛍️ Mon volume perso (PV)</div>
          <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
            <input type="text" inputMode="decimal" value={e.pv} onChange={(ev) => maj({ pv: ev.target.value })}
              placeholder={pvDefaut > 0 ? `Auto : ${fmtE(pvDefaut)}` : "Ex : 120"} style={champ} />
            <span style={{ fontWeight: 700, color: C.brun }}>€</span>
          </div>
          <div style={{ fontSize: ".62rem", color: C.gris, lineHeight: 1.5, marginTop: ".35rem" }}>
            Ce que tu as commandé / vendu sur la période (21 jours). Laisse vide pour reprendre ton chiffre de la tirelire ou de tes objectifs. Minimum {PV_MIN} €.
          </div>
        </div>

        {/* MON ÉQUIPE */}
        <div id="decouverte-equipe-membres" style={bloc}>
          <div style={etiquette}>👥 Les personnes de mon équipe</div>

          {(e.membres || []).length === 0 && (
            <div style={{ fontSize: ".72rem", color: C.gris, lineHeight: 1.55, marginBottom: ".6rem" }}>
              Ajoute les personnes de ton équipe : leur chiffre remonte tout seul. Si quelqu'un ne se met pas à jour, tu peux taper son volume à la main.
            </div>
          )}

          {eff.map((m, idx) => {
            const info = parId.get(m.id);
            const fleches = <FlechesOrdre premier={idx === 0} dernier={idx === eff.length - 1} onHaut={() => deplacer(m.id, -1)} onBas={() => deplacer(m.id, 1)} />;
            const estDir = info && info.type === "directrice";
            const niveau = info && info.niveau;
            const placeholder = (m.source === "auto" || m.source === "arbre-seul") ? `Auto : ${fmtE(m.auto)}` : m.source === "obsolete" ? "À taper" : m.source === "vide" ? "À taper" : "Volume €";
            if (m.exclu) {
              return (
                <div key={m.id} style={{ borderTop: `1px solid ${C.pale}`, padding: ".6rem 0", display: "flex", alignItems: "center", gap: ".5rem" }}>
                  {fleches}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 700, color: C.gris, wordBreak: "break-word" }}>{m.nom}</div>
                    <div style={{ fontSize: ".6rem", color: C.vert, lineHeight: 1.45 }}>✓ Déjà comptée dans le groupe de <strong>{m.inclusDans}</strong> : son volume n'est pas compté une 2e fois. Tu peux la laisser ou la retirer, le résultat est le même.</div>
                  </div>
                  <button onClick={() => retirer(m.id)} aria-label="Retirer" style={{ background: "none", border: "none", color: C.gris, fontSize: "1.05rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
                </div>
              );
            }
            return (
              <div key={m.id} style={{ borderTop: `1px solid ${C.pale}`, padding: ".6rem 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                  {fleches}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 700, color: C.brun, wordBreak: "break-word" }}>{estDir ? "👑 " : ""}{m.nom}</div>
                    <div style={{ fontSize: ".6rem", color: C.gris }}>
                      {estDir ? "Groupe de directrice" : niveau ? `${niveau.nom} · ${fmtPct(niveau.pct)}` : ""}
                    </div>
                  </div>
                  <div style={{ width: 96 }}>
                    <input type="text" inputMode="decimal" value={m.pgvManuel == null ? "" : m.pgvManuel} placeholder={placeholder}
                      onChange={(ev) => majMembre(m.id, { pgvManuel: ev.target.value })}
                      style={{ ...champ, fontSize: ".82rem", padding: ".4rem .5rem", textAlign: "right" }} />
                  </div>
                  <div style={{ minWidth: 62, textAlign: "right", fontSize: ".76rem", fontWeight: 700, color: C.vert }}>
                    {estDir ? (info.prime ? "+" + fmtE(info.prime.gain) : "—") : info ? "+" + fmtE(info.gain) : ""}
                  </div>
                  <button onClick={() => retirer(m.id)} aria-label="Retirer" style={{ background: "none", border: "none", color: C.gris, fontSize: "1.05rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
                </div>

                {(m.source === "manuel" || m.source === "manuel-vide" || m.source === "obsolete" || m.source === "vide") && (m.pgvManuel == null || m.pgvManuel === "") && m.dernierPgv ? (
                  <div style={{ fontSize: ".58rem", color: C.gris, marginTop: ".2rem" }}>Période précédente : {fmtE(nombre(m.dernierPgv))} (à retaper si c'est pareil)</div>
                ) : null}
                {m.source === "auto" && <div style={{ fontSize: ".56rem", color: C.vert, marginTop: ".2rem" }}>✓ Chiffre remonté automatiquement{m.maj ? ` · mis à jour le ${dateCourte(m.maj)}` : ""}</div>}
                {m.source === "auto" && m.nbSous > 0 && (
                  <div style={{ fontSize: ".56rem", color: C.vert, marginTop: ".1rem", lineHeight: 1.45 }}>
                    👥 Son équipe est comprise : {m.nbSous} personne{m.nbSous > 1 ? "s" : ""} sous elle{m.depuisEquipe ? " (volume recalculé à partir de leurs chiffres)" : ""}.
                  </div>
                )}
                {m.source === "arbre-seul" && (
                  <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".2rem", lineHeight: 1.45 }}>
                    ⚠️ Pas mise à jour cette période : on compte seulement ce que son équipe a remonté ({m.nbSous} personne{m.nbSous > 1 ? "s" : ""}). Si tu connais son vrai volume, tape-le.
                  </div>
                )}
                {m.source === "obsolete" && (
                  <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".2rem", lineHeight: 1.45 }}>
                    ⚠️ Pas mise à jour cette période{m.maj ? ` (dernière fois le ${dateCourte(m.maj)})` : ""}. Son chiffre n'est pas compté : tape-le à la main.
                  </div>
                )}
                {m.source === "vide" && <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".2rem" }}>Elle n'a pas encore rempli son chiffre : tape-le à la main.</div>}

                {m.pvOk === false && !estDir && <div style={{ fontSize: ".58rem", color: "#B26A00", marginTop: ".2rem" }}>⚠️ Moins de {PV_MIN} € de commande perso : pas de niveau, son volume te revient entier.</div>}

                {estDir && (
                  <div style={{ marginTop: ".4rem", background: C.creme, borderRadius: 8, padding: ".45rem .55rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: ".4rem", flexWrap: "wrap" }}>
                      <span style={{ fontSize: ".62rem", color: C.gris }}>Ligne :</span>
                      {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => majMembre(m.id, { ligne: n })} style={petitBtn((m.ligne || 1) === n)}>{n}</button>)}
                    </div>
                    <div style={{ fontSize: ".58rem", color: C.gris, marginTop: ".25rem", lineHeight: 1.45 }}>
                      Ligne 1 = directrice directement dans ta structure. Ligne 2 = une directrice juste en dessous d'elle, etc.
                    </div>
                    {(m.ligne || 1) === 2 && (
                      <label style={{ display: "flex", gap: ".4rem", alignItems: "flex-start", marginTop: ".35rem", fontSize: ".6rem", color: C.brun, lineHeight: 1.45, cursor: "pointer" }}>
                        <input type="checkbox" checked={!!m.bonus2} onChange={(ev) => majMembre(m.id, { bonus2: ev.target.checked })} style={{ marginTop: 2 }} />
                        <span>Bonus 2e ligne : le PGV de la directrice de 1re ligne est entre 2 500 € et 3 999 € (+1 %)</span>
                      </label>
                    )}
                  </div>
                )}

                {!estDir && (
                  <label style={{ display: "flex", gap: ".35rem", alignItems: "center", marginTop: ".3rem", fontSize: ".58rem", color: C.gris, cursor: "pointer" }}>
                    <input type="checkbox" checked={m.pvManuel !== "" && m.pvManuel != null && nombre(m.pvManuel) < PV_MIN}
                      onChange={(ev) => majMembre(m.id, { pvManuel: ev.target.checked ? "0" : "" })} />
                    Elle n'a pas atteint {PV_MIN} € de commande perso
                  </label>
                )}
              </div>
            );
          })}

          {/* Recherche dans l'annuaire */}
          <div style={{ marginTop: ".5rem" }}>
            {annuaire === undefined && <div style={{ fontSize: ".66rem", color: C.gris }}>Chargement de l'annuaire…</div>}
            {annuaire === null && <div style={{ fontSize: ".66rem", color: "#B26A00" }}>Annuaire indisponible pour l'instant : tu peux ajouter les personnes à la main ci-dessous.</div>}
            {annuaire && (
              <>
                {directes.length > 0 && (
                  <button onClick={() => ajouterAnnuaire(directes)}
                    style={{ width: "100%", background: `linear-gradient(135deg,${C.rose},${C.or})`, color: "white", border: "none", borderRadius: 10, padding: ".55rem", fontSize: ".76rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginBottom: ".5rem" }}>
                    ✨ Ajouter toutes mes filleules directes ({directes.length})
                  </button>
                )}
                <input type="text" value={recherche} onChange={(ev) => setRecherche(ev.target.value)} placeholder="🔍 Chercher une personne par son prénom ou nom"
                  style={{ ...champ, fontSize: ".8rem", fontWeight: 500 }} />
                {resultats.map((a) => {
                  const chez = dansGroupeDe(a);
                  return (
                    <div key={a.uid} onClick={() => { if (!chez) ajouterAnnuaire([a]); }}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".4rem", padding: ".45rem .3rem", borderBottom: `1px solid ${C.pale}`, cursor: chez ? "default" : "pointer", opacity: chez ? .75 : 1 }}>
                      <span style={{ fontSize: ".78rem", color: C.brun, fontWeight: 600 }}>{nomAnnuaire(a)}</span>
                      <span style={{ fontSize: ".6rem", color: chez || a.marraine !== uid ? C.gris : C.vert, textAlign: "right" }}>
                        {chez ? `Déjà comptée dans le groupe de ${chez}` : `${a.marraine === uid ? "Filleule directe" : "Dans l'annuaire"} · + Ajouter`}
                      </span>
                    </div>
                  );
                })}
                {q.length >= 2 && resultats.length === 0 && <div style={{ fontSize: ".64rem", color: C.gris, padding: ".4rem .2rem" }}>Personne trouvée. Ajoute-la à la main ci-dessous.</div>}
              </>
            )}

            <div style={{ marginTop: ".7rem", paddingTop: ".6rem", borderTop: `1px dashed ${C.pale}` }}>
              <div style={{ fontSize: ".62rem", color: C.gris, marginBottom: ".3rem" }}>Ou ajouter quelqu'un à la main :</div>
              <div style={{ display: "flex", gap: ".4rem" }}>
                <input type="text" value={manuelNom} onChange={(ev) => setManuelNom(ev.target.value)} placeholder="Prénom"
                  style={{ ...champ, fontSize: ".8rem", fontWeight: 500, flex: 1 }} />
                <input type="text" inputMode="decimal" value={manuelPgv} onChange={(ev) => setManuelPgv(ev.target.value)} placeholder="Volume €"
                  onKeyDown={(ev) => { if (ev.key === "Enter") ajouterManuel(); }}
                  style={{ ...champ, fontSize: ".8rem", fontWeight: 500, width: 92 }} />
                <button onClick={ajouterManuel} disabled={!manuelNom.trim()}
                  style={{ background: manuelNom.trim() ? C.rose : C.pale, color: "white", border: "none", borderRadius: 10, padding: "0 .8rem", fontSize: ".9rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+</button>
              </div>
              <div style={{ fontSize: ".58rem", color: C.gris, marginTop: ".3rem", lineHeight: 1.45 }}>Le volume = le chiffre du groupe de cette personne (elle + toute son équipe) sur la période. N'ajoute pas quelqu'un qui est déjà dans l'équipe d'une personne de ta liste : son volume est déjà compris.</div>
            </div>
          </div>
        </div>

        {/* DIRECTRICE */}
        <div id="decouverte-equipe-directrices" style={bloc}>
          <div onClick={() => setDirOuvert((o) => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div style={{ ...etiquette, marginBottom: 0 }}>👑 Directrices dans ma structure</div>
            <span style={{ color: C.gris, transform: dirOuvert ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
          </div>
          {dirOuvert && (
            <div style={{ marginTop: ".6rem", fontSize: ".7rem", color: C.brun, lineHeight: 1.6 }}>
              <div>Une personne dont le groupe atteint <strong>{fmtE(SEUIL_DIRECTEUR)}</strong> (avec 25 € de PV) devient directrice. Son groupe sort de ton PGV : tu touches à la place une <strong>prime de directrice</strong> sur son volume.</div>
              <div style={{ marginTop: ".5rem", background: C.creme, borderRadius: 10, padding: ".55rem .65rem" }}>
                <div>Groupes de directrices en 1re ligne : <strong>{res.nbGroupes1}</strong></div>
                {res.statut && <div>Ton statut : <strong>{res.statut.nom}</strong> (PGV mini {fmtE(res.statut.pgv)} · ton PGV : {fmtE(res.pgv)})</div>}
                {res.panier && <div style={{ color: "#B26A00", marginTop: ".25rem" }}>⚠️ Ton PGV est sous le seuil de ton statut : tu touches 17 % de ton PGV mais pas les primes de directrice.</div>}
                {res.statut && res.statut.lignes.length > 0 && (
                  <div style={{ marginTop: ".3rem" }}>Primes par ligne : {res.statut.lignes.map((p, i) => `L${i + 1} ${fmtPct(p)}`).join(" · ")}</div>
                )}
                {suivantStatut && <div style={{ marginTop: ".3rem", color: C.gris }}>Prochain statut : <strong style={{ color: C.brun }}>{suivantStatut.nom}</strong> ({suivantStatut.groupes} groupes{suivantStatut.elite ? `, dont ${suivantStatut.elite} Élite` : ""}){manqueGroupes > 0 ? ` : il t'en manque ${manqueGroupes}.` : "."}</div>}
              </div>
              {res.nbGroupes1 >= 22 && (
                <div style={{ marginTop: ".5rem", display: "flex", alignItems: "center", gap: ".5rem" }}>
                  <span style={{ fontSize: ".66rem", color: C.gris }}>Dont groupes « Élite » :</span>
                  <input type="number" min="0" value={e.nbElite || 0} onChange={(ev) => maj({ nbElite: Math.max(0, parseInt(ev.target.value, 10) || 0) })}
                    style={{ ...champ, width: 70, fontSize: ".8rem", padding: ".3rem .5rem" }} />
                </div>
              )}
              {res.estDirectrice && res.primeStatut && (
                <div style={{ marginTop: ".5rem", fontSize: ".66rem", color: C.gris }}>
                  🎁 Prime de statut {res.primeStatut.nom} : <strong style={{ color: C.brun }}>{fmtE(res.primeStatut.montant)}</strong>, versée une seule fois si tu gardes ton statut 6 périodes sur 12. Non comptée dans la tirelire.
                </div>
              )}
            </div>
          )}
        </div>

        {/* PRIME PARENTALE */}
        <div style={bloc}>
          <div style={etiquette}>👶 Prime parentale</div>
          <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
            <span style={{ fontSize: ".72rem", color: C.brun, flex: 1, lineHeight: 1.45 }}>Enfants nés ou adoptés dans l'année</span>
            <input type="number" min="0" max="10" value={e.enfants || 0} onChange={(ev) => maj({ enfants: Math.max(0, parseInt(ev.target.value, 10) || 0) })}
              style={{ ...champ, width: 70, fontSize: ".9rem", padding: ".35rem .5rem", textAlign: "center" }} />
          </div>
          <div style={{ fontSize: ".6rem", color: C.gris, lineHeight: 1.5, marginTop: ".35rem" }}>
            40 € par période pour le 1er enfant, 50 € pour les suivants, pendant un an. À partir de « Directrice adjointe » (14 %) tenu 6 périodes dans l'année, avec 25 € de PV et un contrat familial.
            {res.pct < 14 ? " Tu n'y es pas encore : elle n'est pas comptée." : ""}
          </div>
        </div>

        {/* TABLEAU DES NIVEAUX */}
        <div style={bloc}>
          <div onClick={() => setInfosOuvert((o) => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div style={{ ...etiquette, marginBottom: 0 }}>📘 Les niveaux et les primes du plan</div>
            <span style={{ color: C.gris, transform: infosOuvert ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
          </div>
          {infosOuvert && (
            <div style={{ marginTop: ".6rem" }}>
              <div style={{ fontSize: ".62rem", color: C.gris, marginBottom: ".4rem", lineHeight: 1.5 }}>Ton niveau dépend de ton PGV (avec au moins 25 € de PV). Tu touches la différence entre ton % et celui de chaque personne de ton groupe.</div>
              {NIVEAUX.slice(1).map((n) => (
                <div key={n.id} style={{ display: "flex", justifyContent: "space-between", fontSize: ".72rem", padding: ".28rem .2rem", borderTop: `1px solid ${C.pale}`, background: !res.estDirectrice && res.niveau.id === n.id ? "#FFF3E0" : "transparent", fontWeight: res.niveau.id === n.id ? 700 : 400, color: C.brun }}>
                  <span>{n.nom}</span><span>dès {fmtE(n.pgv)} · {fmtPct(n.pct)}</span>
                </div>
              ))}
              <div style={{ fontSize: ".6rem", color: C.gris, lineHeight: 1.55, marginTop: ".6rem" }}>
                <div><strong>Prime de statut</strong> (une fois) : {STATUTS_DIR.slice(0, 5).map((s) => `${s.nom.replace("Directrice ", "")} ${fmtE(s.bonus)}`).join(" · ")} … jusqu'à {fmtE(STATUTS_DIR[STATUTS_DIR.length - 1].bonus)}.</div>
                <div style={{ marginTop: ".3rem" }}><strong>Méga-bonus</strong> et <strong>double bonus</strong> : même montant que la prime de statut, si tu progresses dans les 17 premières périodes après ton inscription et que tu confirmes le résultat sur les périodes suivantes (écart d'au moins 1 000 € de PGV avec ta meilleure responsable).</div>
              </div>
            </div>
          )}
        </div>

        <div style={{ fontSize: ".58rem", color: C.gris, textAlign: "center", lineHeight: 1.55 }}>
          Estimation indicative calculée d'après le plan marketing Mihi. Seul ton relevé officiel Mihi fait foi.
        </div>
    </FeuilleModale>
  );
}
